import React, {useMemo} from 'react';

import {AnalysisTemplateSelectorTemplate} from 'analysis-tool/graphql/AnalysisTemplateSelectorQuery';
import StepConfigEditorRouterV2 from 'pipelines/generalized-step-config-editor/editors/StepConfigEditorRouterV2';
import {makeInitialConfigStateFromConfigShape} from 'pipelines/generalized-step-config-editor/initial-values/makeInitialConfigState';
import {setValueInConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {StepConfigEditorContext} from 'pipelines/generalized-step-config-editor/StepConfigEditorContext';
import {
  ConfigShapePath,
  FIELD_ORDER_UI_SCHEMA_KEY,
  ShapeUISchemaV2,
} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {JSONValue} from 'util/Types';

// TODO(BNCH-132431): Backfill missing variable names
const SYSTEM_VARIABLE_TO_NAME: Record<string, string> = {
  datasetName: 'Dataset name',
  documentId: 'Entry or worksheet',
  folderId: 'Folder',
  fileId: 'File',
  fileIds: 'Files',
};

function getVariableName(variable: AnalysisTemplateSelectorTemplate['configVariables'][number]): string {
  if (variable.name.length > 0) {
    return variable.name;
  }
  return SYSTEM_VARIABLE_TO_NAME[variable.configVariableKey] ?? variable.configVariableKey;
}

function adjustVariableTypeConfigSpec(configSpec: PipelineConfigJsonSpec): PipelineConfigJsonSpec {
  // 'DOCUMENT' is not a searchable type; users should only be able to search for 'ENTRY' and 'WORKSHEET'
  return configSpec.kind === 'domain'
    ? {...configSpec, types: configSpec.types.filter((type) => type !== 'DOCUMENT')}
    : configSpec;
}

interface AnalysisTemplateVariablesFormProps {
  clientStateId: string;
  variables: AnalysisTemplateSelectorTemplate['configVariables'];
  variableBindings: Record<string, JSONValue>;
  onVariableBindingsChange: (bindings: Record<string, JSONValue>) => void;
}

export default function AnalysisTemplateVariablesForm({
  clientStateId,
  variables,
  variableBindings,
  onVariableBindingsChange,
}: AnalysisTemplateVariablesFormProps): JSX.Element {
  const configShape = useMemo(
    () => ({
      kind: 'shape' as const,
      required: true,
      fields: Object.fromEntries(
        variables.map((variable) => [
          variable.id,
          adjustVariableTypeConfigSpec(variable.variableTypeConfigSpec),
        ])
      ),
    }),
    [variables]
  );

  const uiSchema: ShapeUISchemaV2 = useMemo(
    () => ({
      [FIELD_ORDER_UI_SCHEMA_KEY]: variables.map((variable) => variable.id),
      ...Object.fromEntries(
        variables.map((variable) => [
          variable.id,
          {displayName: getVariableName(variable), configShapePath: [variable.id]},
        ])
      ),
    }),
    [variables]
  );

  const emptyVariableBindings = useMemo(
    () => makeInitialConfigStateFromConfigShape(configShape.fields, uiSchema),
    [configShape, uiSchema]
  );

  const editorContext = useMemo(() => ({dataFramesInfos: {}, clientStateId}), [clientStateId]);

  return (
    <section aria-label="Template variables">
      <StepConfigEditorContext.Provider value={editorContext}>
        <StepConfigEditorRouterV2
          configShape={configShape}
          config={{...emptyVariableBindings, ...variableBindings}}
          onChangeConfig={(config: Record<string, JSONValue>, path: ConfigShapePath, newValue: JSONValue) => {
            onVariableBindingsChange(setValueInConfig(config, path, newValue));
          }}
          uiSchema={uiSchema}
        />
      </StepConfigEditorContext.Provider>
    </section>
  );
}
